import { supabase, HistorialEstadoMiembro } from '../lib/supabase';

export interface PeriodoIngreso {
  numeroIngreso: number;
  fechaInicio: string;
  fechaFin: string | null;
  duracion: {
    años: number;
    meses: number;
  };
  esActivo: boolean;
}

export interface AntiguedadCalculada {
  años: number;
  meses: number;
}

export const calcularAntiguedad = (
  fechaInicio: string,
  fechaFin: string | null = null
): AntiguedadCalculada => {
  const inicio = new Date(fechaInicio);
  const fin = fechaFin ? new Date(fechaFin) : new Date();

  let años = fin.getFullYear() - inicio.getFullYear();
  let meses = fin.getMonth() - inicio.getMonth();

  if (meses < 0) {
    años--;
    meses += 12;
  }

  if (fin.getDate() < inicio.getDate()) {
    meses--;
    if (meses < 0) {
      años--;
      meses += 12;
    }
  }

  return {
    años: Math.max(0, años),
    meses: Math.max(0, meses)
  };
};

export const formatearAntiguedad = (años: number, meses: number): string => {
  if (años === 0 && meses === 0) {
    return 'Menos de 1 mes';
  }

  const partes: string[] = [];

  if (años > 0) {
    partes.push(años === 1 ? '1 año' : `${años} años`);
  }

  if (meses > 0) {
    partes.push(meses === 1 ? '1 mes' : `${meses} meses`);
  }

  return partes.join(', ');
};

const reconstruirPeriodos = (
  cambiosEstado: HistorialEstadoMiembro[]
): PeriodoIngreso[] => {
  if (cambiosEstado.length === 0) {
    return [];
  }

  const periodos: PeriodoIngreso[] = [];
  let periodoActual: { inicio: string; numero: number } | null = null;

  cambiosEstado.forEach((cambio) => {
    if (cambio.estado_nuevo === 'activo') {
      periodoActual = {
        inicio: cambio.created_at,
        numero: cambio.numero_ingreso_nuevo
      };
    } else if (cambio.estado_nuevo === 'sin_derechos' && periodoActual) {
      const duracion = calcularAntiguedad(periodoActual.inicio, cambio.created_at);
      periodos.push({
        numeroIngreso: periodoActual.numero,
        fechaInicio: periodoActual.inicio,
        fechaFin: cambio.created_at,
        duracion,
        esActivo: false
      });
      periodoActual = null;
    }
  });

  const periodoFinal = periodoActual as { inicio: string; numero: number } | null;
  if (periodoFinal !== null) {
    const duracion = calcularAntiguedad(periodoFinal.inicio);
    periodos.push({
      numeroIngreso: periodoFinal.numero,
      fechaInicio: periodoFinal.inicio,
      fechaFin: null,
      duracion,
      esActivo: true
    });
  }

  return periodos;
};

export const historialIngresosService = {
  async obtenerHistorialIngresos(miembroId: string): Promise<PeriodoIngreso[]> {
    const { data, error } = await supabase
      .from('historial_estados_miembros')
      .select('*')
      .eq('miembro_id', miembroId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error al obtener historial de estados:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    return reconstruirPeriodos(data);
  },

  async obtenerAntiguedadActual(miembroId: string): Promise<AntiguedadCalculada | null> {
    const periodos = await this.obtenerHistorialIngresos(miembroId);
    const periodoActivo = periodos.find(p => p.esActivo);

    if (!periodoActivo) {
      return null;
    }

    return periodoActivo.duracion;
  }
};
